/**
 * A pipeline's segments (GET /pipeline-facility/sites/:id carries them;
 * POST /sites/:id/segments, PATCH / DELETE /segments/:id).
 *
 * Add and edit happen in an inline form at the top of the panel, never in a
 * modal. Delete asks in the row itself. Writes are the office plus the
 * supervisor (PF_SEGMENT_WRITE); everyone else sees the table only.
 *
 * Chainage, OD and WT are typed and shown in the tenant's units; LENGTH is the
 * API's figure, not to - from computed here. Live segments of one site may
 * not overlap: the API answers 409 `code: 'segment_overlap'` with the segment
 * in the way, and the form names it.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCreatePfSegment, useDeletePfSegment, useUpdatePfSegment } from '../../hooks/usePipelineFacility';
import { blankToNull, fmtNumber, pfErrorMessage, segmentOverlapMessage, toInput } from '../../lib/pipeline-facility';
import type { PfMeta, PfSegment, PfSegmentWrite } from '../../types/pipeline-facility';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfEmpty, PfField, PfFormError, PfPanel } from './PfBits';

interface SegForm {
  label: string;
  from_chainage: string;
  to_chainage: string;
  od: string;
  wt: string;
  grade: string;
  coating_type: string;
  notes: string;
}

const EMPTY: SegForm = { label: '', from_chainage: '', to_chainage: '', od: '', wt: '', grade: '', coating_type: '', notes: '' };
const fromSegment = (s: PfSegment): SegForm => ({
  label: s.label ?? '',
  from_chainage: toInput(s.fromChainage),
  to_chainage: toInput(s.toChainage),
  od: toInput(s.od),
  wt: toInput(s.wt),
  grade: s.grade ?? '',
  coating_type: s.coatingType ?? '',
  notes: s.notes ?? '',
});

function segBody(form: SegForm, initial: SegForm | null): PfSegmentWrite {
  const body: Record<string, unknown> = {};
  (Object.keys(form) as (keyof SegForm)[]).forEach((k) => {
    if (initial && form[k] === initial[k]) return;
    body[k] = blankToNull(form[k]);
  });
  return body as PfSegmentWrite;
}

export function SegmentsPanel({
  siteId,
  segments,
  meta,
  canWrite,
}: {
  siteId: string;
  segments: PfSegment[];
  meta: PfMeta | null;
  canWrite: boolean;
}) {
  const create = useCreatePfSegment();
  const update = useUpdatePfSegment();
  const remove = useDeletePfSegment();
  const [mode, setMode] = useState<null | { kind: 'add' } | { kind: 'edit'; segment: PfSegment }>(null);
  const [form, setForm] = useState<SegForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const u = meta?.units ?? null;
  const cu = u?.chainage ?? null;
  const unit = (x: string | null | undefined) => (x ? ` (${x})` : '');
  const set = (k: keyof SegForm, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const pending = create.isPending || update.isPending;

  const openAdd = () => {
    setForm(EMPTY);
    setError(null);
    setMode({ kind: 'add' });
  };
  const openEdit = (s: PfSegment) => {
    setForm(fromSegment(s));
    setError(null);
    setMode({ kind: 'edit', segment: s });
  };
  const close = () => {
    setMode(null);
    setError(null);
  };

  const onError = (err: unknown) => setError(segmentOverlapMessage(err, segments, cu) ?? pfErrorMessage(err, 'The segment could not be saved.'));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.from_chainage.trim() || !form.to_chainage.trim()) return setError('Enter both the start and the end chainage.');
    if (mode?.kind === 'edit') {
      const body = segBody(form, fromSegment(mode.segment));
      if (Object.keys(body).length === 0) return setError('Nothing has changed.');
      update.mutate(
        { id: mode.segment.id, body },
        { onSuccess: () => { toast.success('Segment saved'); close(); }, onError },
      );
    } else {
      create.mutate(
        { siteId, body: segBody(form, null) },
        { onSuccess: () => { toast.success('Segment added'); close(); }, onError },
      );
    }
  };

  const doDelete = (id: string) => {
    setDeleteError(null);
    remove.mutate(id, {
      onSuccess: () => {
        toast.success('Segment deleted');
        setConfirmDelete(null);
      },
      onError: (err) => setDeleteError(pfErrorMessage(err, 'The segment could not be deleted.')),
    });
  };

  return (
    <PfPanel
      eyebrow="Pipeline"
      title="Segments"
      testId="pf-segments-panel"
      actions={
        canWrite && !mode ? (
          <button type="button" className={PF_SECONDARY} onClick={openAdd}>
            <Plus className="size-4" aria-hidden="true" />
            Add segment
          </button>
        ) : undefined
      }
      footer={segments.length > 0 ? `${segments.length} segment${segments.length === 1 ? '' : 's'}, by start chainage` : undefined}
    >
      {canWrite && mode && (
        <form onSubmit={submit} noValidate className="space-y-3 border-b border-fieldiq-border bg-fieldiq-dark/30 px-4 py-4" data-testid="pf-segment-form">
          <p className="text-sm font-extrabold text-white">
            {mode.kind === 'edit' ? `Edit segment ${mode.segment.label || ''}`.trim() : 'New segment'}
          </p>
          <PfFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <PfField label="Label">
              <input className={PF_INPUT} value={form.label} onChange={(e) => set('label', e.target.value)} aria-label="Segment label" />
            </PfField>
            <PfField label={`From chainage${unit(cu)}`}>
              <input className={PF_INPUT} inputMode="decimal" value={form.from_chainage} onChange={(e) => set('from_chainage', e.target.value)} aria-label="From chainage" />
            </PfField>
            <PfField label={`To chainage${unit(cu)}`}>
              <input className={PF_INPUT} inputMode="decimal" value={form.to_chainage} onChange={(e) => set('to_chainage', e.target.value)} aria-label="To chainage" />
            </PfField>
            <PfField label="Coating">
              <input className={PF_INPUT} value={form.coating_type} onChange={(e) => set('coating_type', e.target.value)} aria-label="Coating" />
            </PfField>
            <PfField label={`OD${unit(u?.diameter)}`}>
              <input className={PF_INPUT} inputMode="decimal" value={form.od} onChange={(e) => set('od', e.target.value)} aria-label="Segment OD" />
            </PfField>
            <PfField label={`WT${unit(u?.wallThickness)}`}>
              <input className={PF_INPUT} inputMode="decimal" value={form.wt} onChange={(e) => set('wt', e.target.value)} aria-label="Segment WT" />
            </PfField>
            <PfField label="Grade">
              <input className={PF_INPUT} value={form.grade} onChange={(e) => set('grade', e.target.value)} aria-label="Segment grade" />
            </PfField>
            <PfField label="Notes">
              <input className={PF_INPUT} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Segment notes" />
            </PfField>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={PF_SECONDARY} onClick={close} disabled={pending}>
              Cancel
            </button>
            <button type="submit" className={PF_PRIMARY} disabled={pending}>
              {pending ? 'Saving…' : mode.kind === 'edit' ? 'Save segment' : 'Add segment'}
            </button>
          </div>
        </form>
      )}

      {segments.length === 0 ? (
        <PfEmpty>This pipeline has no segments yet.</PfEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Segment</th>
                <th scope="col" className="px-4 py-3 text-right">From{unit(cu)}</th>
                <th scope="col" className="px-4 py-3 text-right">To{unit(cu)}</th>
                <th scope="col" className="px-4 py-3 text-right">Length{unit(cu)}</th>
                <th scope="col" className="px-4 py-3 text-right">OD{unit(u?.diameter)}</th>
                <th scope="col" className="px-4 py-3 text-right">WT{unit(u?.wallThickness)}</th>
                <th scope="col" className="px-4 py-3">Grade</th>
                <th scope="col" className="px-4 py-3">Coating</th>
                {canWrite && <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {segments.map((s) => (
                <tr key={s.id} data-testid="pf-segment-row" className="border-t border-fieldiq-border">
                  <td className="px-4 py-3 text-sm font-bold text-white">
                    {s.label || '—'}
                    {s.notes && <p className="mt-0.5 text-xs font-normal text-fieldiq-muted">{s.notes}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtNumber(s.fromChainage)}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtNumber(s.toChainage)}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtNumber(s.length)}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtNumber(s.od)}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtNumber(s.wt)}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{s.grade || '—'}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{s.coatingType || '—'}</td>
                  {canWrite && (
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {confirmDelete === s.id ? (
                        <span className="inline-flex flex-wrap items-center justify-end gap-2">
                          <span className="text-xs text-fieldiq-text">Delete this segment?</span>
                          <button type="button" className={PF_SECONDARY} onClick={() => doDelete(s.id)} disabled={remove.isPending}>
                            {remove.isPending ? 'Deleting…' : 'Delete'}
                          </button>
                          <button type="button" className={PF_SECONDARY} onClick={() => { setConfirmDelete(null); setDeleteError(null); }}>
                            Keep
                          </button>
                          {deleteError && (
                            <span role="alert" className="basis-full text-right text-xs text-fieldiq-text">
                              {deleteError}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex gap-2">
                          <button type="button" className={PF_SECONDARY} onClick={() => openEdit(s)} aria-label={`Edit segment ${s.label ?? ''}`.trim()}>
                            <Pencil className="size-4" aria-hidden="true" />
                            Edit
                          </button>
                          <button
                            type="button"
                            className={PF_SECONDARY}
                            onClick={() => { setDeleteError(null); setConfirmDelete(s.id); }}
                            aria-label={`Delete segment ${s.label ?? ''}`.trim()}
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                            Delete
                          </button>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PfPanel>
  );
}
